import React, { useEffect, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { ViewportDrawer } from '../Overlay';
import { SetAvatar, SetCheck, SetChip, SetInput, SetSwitch, SetTextarea, SetTriState } from '../SettingsShell/controls';
import { CATEGORY_COLOUR, CATEGORY_LABEL } from '../../settings-booking/types';
import type { AppointmentReason, BookableBy, ReasonSentence } from '../../settings-booking/types';
import { ROLES } from '../../settings-practice/types';
import type { Provider, Role } from '../../settings-practice/types';

export interface ReasonDrawerProps {
  reason: AppointmentReason | null;
  holders: Map<Role, Provider[]>;
  sentences: ReasonSentence[];
  onClose: () => void;
  onSave: (next: AppointmentReason) => void;
  onAddSentence?: () => void;
}

const BOOKABLE_OPTIONS: { value: BookableBy; label: string; tone: 'muted' | 'amber' | 'green' }[] = [
  { value: 'staff_only', label: 'Staff only', tone: 'muted' },
  { value: 'elva_and_staff', label: 'ELVA & staff', tone: 'amber' },
  { value: 'anyone', label: 'Anyone', tone: 'green' },
];

/** Edit a reason (spec 15 §8.3). Right drawer, explicit save. */
export function ReasonDrawer({ reason, holders, sentences, onClose, onSave, onAddSentence }: ReasonDrawerProps) {
  const [r, setR] = useState<AppointmentReason | null>(reason);
  useEffect(() => { setR(reason); }, [reason]);

  if (!r) {
    return <ViewportDrawer open={false} onClose={onClose} width="min(640px, 96vw)">{null}</ViewportDrawer>;
  }

  const colour = CATEGORY_COLOUR[r.category];
  const faces = Array.from(new Set(r.roles.flatMap((role) => holders.get(role) ?? [])));

  return (
    <ViewportDrawer open={reason !== null} onClose={onClose} width="min(640px, 96vw)">
      <div className="flex h-full flex-col bg-[var(--surface-card)]">
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-6 py-5">
          <div className="flex items-start gap-3">
            <span
              className="mt-0.5 rounded-[7px] px-2 py-1 font-[var(--font-mono)] text-[11px] font-bold"
              style={{ background: `${colour}1F`, color: colour }}
            >
              {r.code}
            </span>
            <div>
              <h2 className="m-0 text-[18px] font-bold tracking-[-0.02em] text-[var(--text-primary)]">
                {r.displayName}
              </h2>
              <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-muted)]">
                {CATEGORY_LABEL[r.category]} · {r.durationMinutes} min ·{' '}
                <strong className="text-[var(--text-secondary)]">
                  {r.roles.length} {r.roles.length === 1 ? 'role' : 'roles'}
                </strong>
                , {faces.length} providers
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-[10px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
          >
            <Icon kind="x" size={14} stroke={2} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <Section title="Identity">
            <Label>Display name — what the patient sees</Label>
            <SetInput ariaLabel="Display name" value={r.displayName} onChange={(v) => { setR({ ...r, displayName: v }); }} />
            <div className="mt-3 flex gap-4">
              <div className="w-[140px]">
                <Label>Code</Label>
                <SetInput ariaLabel="Code" mono value={r.code} onChange={(v) => { setR({ ...r, code: v }); }} />
              </div>
              <div className="flex-1">
                <Label>Category</Label>
                <div className="pt-2 text-[13px] text-[var(--text-primary)]">{CATEGORY_LABEL[r.category]}</div>
              </div>
            </div>
          </Section>

          <Section
            title="What patients say"
            desc={
              <>
                <strong className="text-[var(--text-secondary)]">Sentences a person mapped to this reason</strong>, and
                what ELVA reads as worked examples when it has to choose. A whole sentence carries the thing that
                decides the answer — a keyword does not.
              </>
            }
          >
            {sentences.length === 0 ? (
              <p className="m-0 rounded-[10px] bg-[#FBF3E4] px-3 py-2.5 text-[11.5px] leading-[1.6] text-[var(--warn-amber)]">
                <strong>This reason has no sentences at all.</strong> With nothing to match on, ELVA has only the
                descriptions below to go by.
              </p>
            ) : (
              sentences.map((s) => (
                <div key={s.id} className="py-2" style={{ borderBottom: '1px solid #edf0f6' }}>
                  <div className="text-[13px] text-[var(--text-primary)]">&ldquo;{s.text}&rdquo;</div>
                  {s.condition ? (
                    <div className="mt-0.5 text-[11px] text-[var(--warn-amber)]">
                      <strong>only when</strong> {s.condition}
                    </div>
                  ) : null}
                  <div className="mt-1 text-[10.5px] text-[var(--text-muted)]">
                    {s.source.replace(/_/g, ' ')} · {s.author}
                  </div>
                </div>
              ))
            )}
            <button
              type="button"
              onClick={onAddSentence}
              className="mt-3 text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
            >
              Add a sentence
            </button>
          </Section>

          <Section title="Descriptions">
            <Label>Shown to the patient while booking</Label>
            <SetTextarea
              ariaLabel="Patient description"
              rows={2}
              value={r.patientDescription ?? ''}
              onChange={(v) => { setR({ ...r, patientDescription: v }); }}
            />
            <div className="mt-3">
              <Label>For ELVA — read after the sentences</Label>
              <SetTextarea
                ariaLabel="ELVA description"
                rows={3}
                value={r.elvaDescription ?? ''}
                onChange={(v) => { setR({ ...r, elvaDescription: v }); }}
              />
            </div>
          </Section>

          <Section title="Length">
            <div className="flex flex-wrap gap-4">
              <div className="w-[120px]">
                <Label>Duration</Label>
                <SetInput
                  ariaLabel="Duration"
                  value={String(r.durationMinutes)}
                  onChange={(v) => { setR({ ...r, durationMinutes: Number(v) || 0 }); }}
                />
              </div>
              <div className="w-[120px]">
                <Label>Buffer after</Label>
                <SetInput
                  ariaLabel="Buffer after"
                  value={String(r.bufferAfterMinutes)}
                  onChange={(v) => { setR({ ...r, bufferAfterMinutes: Number(v) || 0 }); }}
                />
              </div>
              <div className="min-w-[200px] flex-1">
                <Label>Earliest it can be booked</Label>
                <SetInput
                  ariaLabel="Earliest it can be booked"
                  value={r.earliestBookable}
                  onChange={(v) => { setR({ ...r, earliestBookable: v }); }}
                />
              </div>
            </div>
            <p className="m-0 mt-3 rounded-[10px] bg-[var(--surface-sunken)] px-3 py-2.5 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
              <strong>Earliest is new.</strong> Nothing today stops ELVA offering a 120-minute implant placement for
              tomorrow morning. A reason that needs lab work, a room turned over or a pre-med says so here,{' '}
              <strong>once</strong>, instead of a person catching it afterwards.
            </p>
          </Section>

          <Section
            title="Who can do it"
            desc={
              <>
                <strong className="text-[var(--text-secondary)]">Roles, never people.</strong> A new hygienist takes
                cleanings the day she appears and nobody reopens this panel.
              </>
            }
          >
            <div className="flex flex-wrap gap-2">
              {ROLES.map((role) => {
                const checked = r.roles.includes(role.id);
                const empty = (holders.get(role.id) ?? []).length === 0;
                return (
                  <span key={role.id} className="inline-flex items-center gap-1">
                    <SetCheck
                      checked={checked}
                      onChange={(next) => {
                        setR({
                          ...r,
                          roles: next ? [...r.roles, role.id] : r.roles.filter((x) => x !== role.id),
                        });
                      }}
                    >
                      {role.label}
                      {empty ? (
                        <span className="ml-1.5 text-[10.5px] font-semibold text-[var(--warn-amber)]">nobody</span>
                      ) : null}
                    </SetCheck>
                  </span>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-[10px] bg-[var(--brand-indigo-tint)] px-3 py-2.5">
              <span className="text-[11.5px] font-bold text-[var(--text-secondary)]">Which means right now</span>
              <span className="flex items-center -space-x-1">
                {faces.map((f) => (
                  <SetAvatar key={f.id} initials={f.initials} colour={f.colour} size={20} />
                ))}
              </span>
              <span className="text-[11.5px] text-[var(--text-secondary)]">
                {faces.length === 0 ? 'Nobody — this reason takes no bookings.' : faces.map((f) => f.elvaSays).join(', ')}
              </span>
            </div>
          </Section>

          <Section title="Who can book it">
            <SetTriState
              name={`bookable-${r.id}`}
              value={r.bookableBy}
              options={
                r.category === 'emergency'
                  ? BOOKABLE_OPTIONS.map((o) => (o.value === 'anyone' ? { ...o, disabled: true } : o))
                  : BOOKABLE_OPTIONS
              }
              onChange={(v) => { setR({ ...r, bookableBy: v }); }}
            />
            {r.category === 'emergency' ? (
              <p className="m-0 mt-2 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
                Emergencies aren&rsquo;t booked online.
              </p>
            ) : r.bookableBy === 'anyone' ? (
              <div className="mt-3 rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-sunken)] px-3.5 py-3">
                <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-[var(--text-muted)]">
                  Online for
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <SetCheck
                    checked={r.onlineForNew !== false}
                    onChange={(v) => { setR({ ...r, onlineForNew: v }); }}
                  >
                    New patients
                  </SetCheck>
                  <SetCheck
                    checked={r.onlineForReturning !== false}
                    onChange={(v) => { setR({ ...r, onlineForReturning: v }); }}
                  >
                    Returning patients
                  </SetCheck>
                </div>
              </div>
            ) : null}
            <p className="m-0 mt-3 rounded-[10px] bg-[var(--brand-indigo-tint)] px-3 py-2.5 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
              <strong>Three answers, not two.</strong> The built page asks only whether a reason is on the online
              booking page, which leaves the product question unanswered:{' '}
              <strong>when a patient rings up about their crown, may ELVA give them an appointment?</strong>
            </p>
          </Section>

          <Section
            title="CDT codes"
            desc="What the rest of ELVA reads to know what this visit was: post-op checks, Treatment Intelligence, the fee estimate."
          >
            <div className="flex flex-wrap items-center gap-1.5">
              {r.cdtCodes.map((c) => (
                <SetChip key={c}>{c}</SetChip>
              ))}
              <button type="button" className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
                Add code
              </button>
            </div>
          </Section>

          <Section title="Status">
            <div className="flex items-start justify-between gap-6">
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold text-[var(--text-primary)]">Bookable</div>
                <p className="m-0 mt-1 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
                  Turned off, it disappears from everywhere new bookings are made.{' '}
                  <strong className="text-[var(--text-secondary)]">
                    Appointments already on the calendar are left alone
                  </strong>{' '}
                  — nothing is cancelled and nothing is renamed.
                </p>
              </div>
              <SetSwitch label="Bookable" checked={r.bookable} onChange={(v) => { setR({ ...r, bookable: v }); }} />
            </div>
          </Section>
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-6 py-4">
          <button type="button" className="text-[12.5px] font-semibold text-[var(--alert-red)] hover:underline">
            Delete
          </button>
          <span className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-[13.5px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-header)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { onSave(r); }}
              className="rounded-[10px] bg-[var(--brand-indigo)] px-4 py-2 text-[13.5px] font-semibold text-white"
            >
              Save
            </button>
          </span>
        </div>
      </div>
    </ViewportDrawer>
  );
}

function Section({ title, desc, children }: { title: string; desc?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="border-b border-[var(--border-default)] px-6 py-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">{title}</div>
      {desc ? <p className="m-0 mt-1.5 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{desc}</p> : null}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-[var(--text-muted)]">
      {children}
    </div>
  );
}
